import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Select, Text } from '~/components';
import type { RootStackParamList } from '~/navigation/types';
import * as yup from 'yup';
import useAppNavigation from '~/hooks/useAppNavigation';
import { Components } from '~/scenes/HiredInsurance/components';
import { useForm } from 'react-hook-form';
import { cancelContractMarisaTakesCareUseCase } from '~/useCases/MarisaTakesCare/CancelContractMarisaTakesCareUseCase';
import useToast from '~/hooks/useToast';
import DefaultContainer from '../../components/DefaultContainer';
import { yupResolver } from '@hookform/resolvers/yup';
import { getCancelReasonsUseCase } from '~/useCases/AssegurouUseCases/GetCancelReasonsUseCase';
import { HiredInsuranceProductType } from '~/domain/Assegurou/contractedServices';

export const schema = yup
  .object()
  .shape({
    cancelReasonId: yup.string().required('Este campo é obrigatório'),
  })
  .required();

const HiredInsuranceCancelFailTakesCare: React.FC = () => {
  const { goBack, navigate } = useAppNavigation();
  const { showError } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ cancelReasonId: string }>({
    resolver: yupResolver(schema),
  });

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<
        RootStackParamList,
        'MarisaServicos_HiredInsuranceCancelFailTakesCare'
      >
    >();

  const [loading, setLoading] = useState(true);
  const [cancelReasons, setCancelReasons] = useState<
    { label: string; value: string }[] | []
  >([]);

  const cancelContract = useCallback(
    async (data: { cancelReasonId: string }) => {
      const responseSuccess = await cancelContractMarisaTakesCareUseCase({
        certificateCode,
        cancelReasonCode: data.cancelReasonId,
      });

      if (responseSuccess) {
        navigate('MarisaServicos_HiredInsuranceCancelSuccessTakesCare');
      } else {
        showError({
          title: 'Ops!',
          description: 'Um erro ocorreu. Tente novamente.',
        });
      }
    },
    [certificateCode, navigate, showError]
  );

  const fetchCancelReasons = useCallback(async () => {
    setLoading(true);
    try {
      const cancelReasonsResponse = await getCancelReasonsUseCase(
        HiredInsuranceProductType.MARISA_TAKES_CARE
      );
      const formattedCancelReasons = cancelReasonsResponse.map((reason) => ({
        label: reason.description,
        value: reason.code,
      }));
      setCancelReasons(formattedCancelReasons);
    } catch (error) {
      showError({
        title: 'Erro',
        description:
          'Houve um erro ao tentar buscar os motivos do cancelamento. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchCancelReasons();
  }, [fetchCancelReasons]);

  return (
    <DefaultContainer
      type="marisa-cuida"
      className="items-center justify-center py-10"
    >
      <Text className="text-lg text-gray-600">Cancelamento</Text>

      <Components.CancelStatus type="alert" />

      <Text className="text-lg text-gray-700 text-center mb-[22px]">
        Tivemos um problema no cancelamento. Tente novamente.
      </Text>

      <Text className="text-sm text-gray-400 mb-[38px]">
        Ocorreu uma falha inesperada.
      </Text>

      <Select
        control={control}
        loading={loading}
        className="text-sm mb-4"
        label="Caso queira mesmo cancelar, selecione uma opção abaixo:"
        name="cancelReasonId"
        placeholder="Selecione o motivo do cancelamento"
        error={errors.cancelReasonId?.message}
        items={cancelReasons}
        containerStyle={{ flex: 0.3 }}
      />

      <Button className="w-full mb-4" onPress={handleSubmit(cancelContract)}>
        Tente novamente
      </Button>

      <Button variant="tertiary" onPress={goBack}>
        VOLTAR
      </Button>
    </DefaultContainer>
  );
};

export default HiredInsuranceCancelFailTakesCare;
